import { mergeFieldValues } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValues,
  getComponentSubtree,
  setComponentSubtree,
  stripComponents,
} from 'features/libra/ontology/ontologyFieldTypes'

import type { FieldAccessScopePath } from './useScopedFieldAccess'

/**
 * The ONE place a scoped fieldValues merge is computed. Both writers --
 * `TicketAccessContext.setFieldValues` (ticket scope) and
 * `useScopedFieldAccess` (component scope) -- delegate here, so the
 * reserved-subtree preservation rules cannot drift between them.
 *
 * Ticket scope: strips the reserved `components` subtree from BOTH sides
 * before merging (a caller accidentally passing a `components` key at the
 * top level cannot stomp per-component state; `mergeFieldValues` treats
 * every top-level key as a `FieldValue` and would corrupt the nested map),
 * then re-attaches the existing subtree untouched.
 *
 * Component scope: merges into only that component's slice, materializing
 * the entry on first write. `mergeFieldValues` preserves wrapper metadata
 * (source citation, llmValue lineage) that a plain spread would drop.
 */
export function buildFieldValuesPatch(
  latestFieldValues: FieldValues,
  scope: FieldAccessScopePath,
  values: Partial<FieldValues>
): FieldValues {
  if (scope.kind === 'ticket') {
    const existingComponents = getComponentSubtree(latestFieldValues)
    const mergedTopLevel = mergeFieldValues(
      stripComponents(latestFieldValues),
      stripComponents(values as FieldValues)
    )
    return existingComponents
      ? setComponentSubtree(mergedTopLevel, existingComponents)
      : mergedTopLevel
  }

  const { componentId } = scope
  const existingComponents = getComponentSubtree(latestFieldValues) ?? {}
  const mergedComponentFields = mergeFieldValues(
    existingComponents[componentId] ?? {},
    values as FieldValues
  )
  return setComponentSubtree(latestFieldValues, {
    ...existingComponents,
    [componentId]: mergedComponentFields,
  })
}
